import { useUserConfig } from '@littlebird/sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { electronApis } from '@/features/electron-api';
import { FEATURES, useFeature } from '@/features/feature-flags';
import {
  resolvePreferredTranscriptionEngine,
  setPreferredTranscriptionEngine,
} from '@/lib/meetings/transcription-engine';
import { isMac } from '@/utils/capabilities';

import { SettingsSection } from '../settings-section';

import {
  formatLocalTranscriptionReadiness,
  localTranscriptionProgressFraction,
} from './settings-meetings-local-transcription.utils';

import type { PreferredTranscriptionEngine } from '@electron/shared/meetings.types';

const LOCAL_TRANSCRIPTION_READINESS_QUERY_KEY = [
  'meeting-local-transcription-readiness',
] as const;

const ENGINE_LABELS: Record<PreferredTranscriptionEngine, string> = {
  remote: 'Remote',
  local: 'Local',
};

export const PreferredTranscriptionEngineOption = observer(() => {
  const hasFeature = useFeature();
  const enabled =
    hasFeature(FEATURES.LOCAL_TRANSCRIPTION_ENGINE_PICKER) &&
    hasFeature(FEATURES.LOCAL_TRANSCRIPTION) &&
    isMac();
  const queryClient = useQueryClient();
  const userConfig = useUserConfig();

  const { data: readiness } = useQuery({
    queryKey: LOCAL_TRANSCRIPTION_READINESS_QUERY_KEY,
    queryFn: () => electronApis.meetings.getLocalTranscriptionReadiness(),
    enabled,
    retry: false,
  });

  useEffect(() => {
    if (!enabled) return;

    return electronApis.meetings.listenLocalTranscriptionReadinessChanged(
      (nextReadiness) => {
        queryClient.setQueryData(
          LOCAL_TRANSCRIPTION_READINESS_QUERY_KEY,
          nextReadiness
        );
      }
    );
  }, [enabled, queryClient]);

  const retryMutation = useMutation({
    mutationKey: ['meeting-local-transcription-readiness-retry'],
    mutationFn: () => electronApis.meetings.retryLocalTranscriptionReadiness(),
    onSuccess: (nextReadiness) => {
      queryClient.setQueryData(
        LOCAL_TRANSCRIPTION_READINESS_QUERY_KEY,
        nextReadiness
      );
    },
    onError: () => {
      toast.error('Failed to retry Local Transcription preparation');
    },
  });

  if (!enabled || !readiness) return null;

  const selectedEngine = resolvePreferredTranscriptionEngine();
  const localUnavailable = readiness.state === 'unavailable';
  const downloadFraction = localTranscriptionProgressFraction(readiness);

  const selectEngine = (engine: PreferredTranscriptionEngine) => {
    if (engine === 'local' && localUnavailable) return;
    setPreferredTranscriptionEngine(userConfig, engine);
  };

  return (
    <>
      <SettingsSection.Item searchId="meetings.transcription-engine">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>
            Preferred Transcription Engine
          </SettingsSection.ItemLabel>
          <SettingsSection.ItemDescription>
            Choose where speech recognition runs for meetings
          </SettingsSection.ItemDescription>
          <p className="text-xs text-foreground-muted">
            {formatLocalTranscriptionReadiness(readiness)}
          </p>
          {downloadFraction !== null && (
            <ProgressBar
              value={downloadFraction}
              label={`Preparing ${readiness.model.displayName}`}
              className="mt-1.5 max-w-[280px]"
            />
          )}
          {readiness.state === 'failed' && readiness.retryable && (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => retryMutation.mutate()}
              isLoading={retryMutation.isPending}
            >
              Retry
            </Button>
          )}
        </SettingsSection.ItemHeader>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <span>{ENGINE_LABELS[selectedEngine]}</span>
            <Icons.chevronDown className="size-3" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            align="end"
            side="bottom"
            className="w-[13rem] gap-2 mt-1"
          >
            <DropdownMenu.Item onSelect={() => selectEngine('remote')}>
              {ENGINE_LABELS.remote}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              disabled={localUnavailable}
              onSelect={() => selectEngine('local')}
            >
              {ENGINE_LABELS.local}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </SettingsSection.Item>
      <SettingsSection.Seperator />
    </>
  );
});
